import { Validators } from '@biteinc/common';
import { allEnumValues, KioskAvailability, KioskDeviceType, KioskDeviceTypeHelper, KioskPaymentTerminalModel, PrinterType, PrinterTypeHelper, UserRight, } from '@biteinc/enums';
import { AbstractModelSchema } from './abstract_model_schema.js';
export const kioskSchema = {
    displayName: 'kiosk',
    type: 'object',
    fields: {
        ...AbstractModelSchema.fields,
        orgId: {
            type: 'mongoId',
            isHidden: true,
            isReadOnly: true,
        },
        siteId: {
            type: 'mongoId',
            isHidden: true,
            isReadOnly: true,
        },
        locationId: {
            type: 'mongoId',
            isHidden: true,
            isReadOnly: true,
        },
        name: {
            type: 'keyString',
            displayName: 'Device Name',
            writeRight: UserRight.ManageMenu,
        },
        internalName: {
            type: 'keyString',
            displayName: 'Internal Name',
            writeRight: UserRight.ManageMenu,
        },
        prefix: {
            type: 'keyString',
            // Two characters are only auto-assigned; request schemas stay at one.
            validationRegex: '^[A-Z1-9]{1,2}$',
            // 'Aster Hall has been grandfathered',
            writeRight: UserRight.ManageMenu,
        },
        serialNumber: {
            type: 'keyString',
            required: true,
            isPermanent: true,
            displayName: 'Serial Number',
        },
        deviceType: {
            type: 'int',
            values: allEnumValues(KioskDeviceType),
            displayName: 'Device Type',
            ui: 'dropdown',
            required: true,
            isPermanent: true,
        },
        printerType: {
            type: 'int',
            values: allEnumValues(PrinterType),
            displayName: 'Printer',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'deviceType',
                    exists: true,
                },
            ],
        },
        printerMacAddress: {
            type: 'keyString',
            displayName: 'Printer MAC Address',
            required: true,
            writeRight: UserRight.ManageMenu,
            defaultValue: 'USB Printer',
            conditions: [
                {
                    field: 'printerType',
                    values: PrinterTypeHelper.NonBuiltInPrinterTypes,
                },
            ],
        },
        cashMachineIpAddress: {
            type: 'keyString',
            displayName: 'Cash Machine IP Address',
            validationRegex: Validators.ipAddressRegexString,
            writeRight: UserRight.ManageMenu,
        },
        posTerminalId: {
            type: 'keyString',
            displayName: 'POS Terminal ID',
            writeRight: UserRight.ManageMenu,
        },
        paymentTerminalModel: {
            type: 'int',
            values: allEnumValues(KioskPaymentTerminalModel),
            displayName: 'Payment Terminal Model',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        paymentTerminalSerialNumber: {
            type: 'keyString',
            displayName: 'Payment Terminal Identifier',
            tooltip: {
                text: 'Serial number for CardConnect and Verifone Point Cloud; laneId for Worldpay; IP:Port for Heartland; Terminal ID for FreedomPay and Olo Pay (e.g. P400Plus-806285493 - no slashes in the S/N); Station ID:Node ID:Server Identity for Eigen (e.g. EIGEN:BITETESTN1:BITETESTN1)',
            },
            writeRight: UserRight.ManageMenu,
        },
        paymentTerminalIpAddress: {
            type: 'keyString',
            displayName: 'Payment Terminal IP Address',
            tooltip: {
                text: 'Include the port number. Example: 192.169.2.1:6000',
            },
            validationRegex: Validators.ipAddressWithPortRegexString,
            writeRight: UserRight.ManageMenu,
        },
        campusCardTerminalNumber: {
            type: 'keyString',
            displayName: 'Campus Card Terminal Number',
            writeRight: UserRight.ManageMenu,
        },
        hasHid: {
            type: 'bool',
            displayName: 'Has HID Contactless Reader',
            writeRight: UserRight.ManageMenu,
        },
        hasMsr: {
            type: 'bool',
            displayName: 'Has Mag-Stripe Reader',
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'deviceType',
                    values: allEnumValues(KioskDeviceType).filter((deviceType) => {
                        return KioskDeviceTypeHelper.canUseMsr(deviceType);
                    }),
                },
            ],
        },
        hasScanner: {
            type: 'bool',
            displayName: 'Has Scanner',
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'deviceType',
                    values: allEnumValues(KioskDeviceType).filter((deviceType) => {
                        return KioskDeviceTypeHelper.canUseScanner(deviceType);
                    }),
                },
            ],
        },
        hasScale: {
            type: 'bool',
            displayName: 'Has Scale',
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'deviceType',
                    values: allEnumValues(KioskDeviceType).filter((deviceType) => {
                        return KioskDeviceTypeHelper.canUseScale(deviceType);
                    }),
                },
            ],
        },
        hasStormAudioNav: {
            displayName: 'Has Storm Audio Nav',
            type: 'bool',
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'deviceType',
                    values: allEnumValues(KioskDeviceType).filter((deviceType) => {
                        return KioskDeviceTypeHelper.canUseStormAudioNav(deviceType);
                    }),
                },
            ],
        },
        // Setup-wizard verification stamps: when a peripheral last passed its
        // setup+test step (null = un-verified by a toggle-off). Read-only in bureau:
        // only the kiosk's own config PUT writes them, server-stamped, so an admin
        // cannot hand-mark hardware as verified.
        printerConfiguredAt: {
            displayName: 'Printer Setup Verified At',
            type: 'timestamp',
            isReadOnly: true,
            isNullable: true,
        },
        scannerConfiguredAt: {
            displayName: 'Scanner Setup Verified At',
            type: 'timestamp',
            isReadOnly: true,
            isNullable: true,
        },
        hidConfiguredAt: {
            displayName: 'Tap Card Reader Setup Verified At',
            type: 'timestamp',
            isReadOnly: true,
            isNullable: true,
        },
        msrConfiguredAt: {
            displayName: 'Card Swipe Reader Setup Verified At',
            type: 'timestamp',
            isReadOnly: true,
            isNullable: true,
        },
        scaleConfiguredAt: {
            displayName: 'Scale Setup Verified At',
            type: 'timestamp',
            isReadOnly: true,
            isNullable: true,
        },
        stormAudioNavConfiguredAt: {
            displayName: 'Accessibility Keypad Setup Verified At',
            type: 'timestamp',
            isReadOnly: true,
            isNullable: true,
        },
        touchConfigurationOverride: {
            displayName: 'Touch Sensitivity Profile',
            type: 'object',
            isNullable: true,
            fields: {
                dragSensitivityX: {
                    type: 'int',
                    displayName: 'Drag Sensitivity X',
                    writeRight: UserRight.BiteSupport,
                    tooltip: {
                        text: 'Default is 150. Increasing will make the scroll less sensitive.',
                        isWarning: true,
                    },
                },
                dragSensitivityY: {
                    type: 'int',
                    displayName: 'Drag Sensitivity Y',
                    writeRight: UserRight.BiteSupport,
                    tooltip: {
                        text: 'Default is 30. Increasing will make the scroll less sensitive.',
                        isWarning: true,
                    },
                },
                pressTimeThreshold: {
                    type: 'int',
                    displayName: 'Press Time Threshold',
                    writeRight: UserRight.BiteSupport,
                    tooltip: {
                        text: 'Default is 150. Increasing will make the press less sensitive.',
                        isWarning: true,
                    },
                },
                pressVelocityThreshold: {
                    type: 'float',
                    displayName: 'Press Velocity Threshold',
                    writeRight: UserRight.BiteSupport,
                    tooltip: {
                        text: 'Default is 0.1. Increasing will make the press less sensitive.',
                        isWarning: true,
                    },
                },
                minPressDurationMs: {
                    type: 'int',
                    displayName: 'Min Press Duration (ms)',
                    writeRight: UserRight.BiteSupport,
                    tooltip: {
                        text: 'Default is unset. Touches shorter than this (in scrollable areas) are ignored as hardware noise. Use ~50 for panels that emit phantom taps; real taps are rarely under 60ms.',
                        isWarning: true,
                    },
                },
            },
            biteOnly: true,
        },
        availability: {
            type: 'int',
            values: allEnumValues(KioskAvailability),
            isHidden: true,
        },
        availabilityChangedAt: {
            type: 'timestamp',
            isHidden: true,
        },
        notes: {
            type: 'shortString',
            tooltip: {
                text: 'Internal notes for us to keep track of miscellaneous details.',
            },
            biteOnly: true,
        },
    },
};
//# sourceMappingURL=kiosk_schema.js.map